import { SubscriptionPlanPrice } from '@/core/api/entities/account/subscriptionPlanPrice'
import {
	PRODUCT_BOX_ADDON_ID,
	PRODUCT_BOX_ONE_SHOT_ID,
	PRODUCT_PLAYER_ID,
	PRODUCT_SOUNDREEF_ADDON_ID,
} from '@/core/api/entities/misc/product'

export class SubscriptionPlan {
	public id: number
	public name: string
	public description: string | null
	public prices: SubscriptionPlanPrice[]
	public metadata: SubscriptionPlanMetadata | null

	constructor(data: {
		id: number
		name: string
		description: string | null
		prices: SubscriptionPlanPrice[]
		metadata: SubscriptionPlanMetadata | null
	}) {
		this.id = data.id
		this.name = data.name
		this.description = data.description
		this.prices = data.prices
		this.metadata = data.metadata
	}

	static create(data: Partial<SubscriptionPlan>) {
		const defaultData = {
			id: 0,
			name: '',
			description: null,
			prices: [],
			metadata: null,
		}

		return new SubscriptionPlan({ ...defaultData, ...data })
	}

	// Return the prices of the product received
	getItemPrices(productId: number): SubscriptionPlanPrice[] {
		return this.prices.filter((price) => price.productId === productId)
	}

	// Return the price of the zone
	getZonePrice(currency?: string): SubscriptionPlanPrice | null {
		if (currency) {
			return (
				this.getItemPrices(PRODUCT_PLAYER_ID).find(
					(price) => price.currency === currency
				) || null
			)
		}

		return this.getItemPrices(PRODUCT_PLAYER_ID)[0]
	}

	// Return the price of the soundreef addon
	getSoundreefPrice(): SubscriptionPlanPrice | null {
		return this.getItemPrices(PRODUCT_SOUNDREEF_ADDON_ID)[0]
	}

	// Return the price of the box addon
	getBoxPrice(): SubscriptionPlanPrice | null {
		return this.getItemPrices(PRODUCT_BOX_ADDON_ID)[0]
	}

	// Return the price of the box one shot
	getBoxOneShotPrice(): SubscriptionPlanPrice | null {
		return this.getItemPrices(PRODUCT_BOX_ONE_SHOT_ID)[0]
	}
}

export class SubscriptionPlanMetadata {
	public canRequestPlaylists: boolean
	public canRequestSchedules: boolean
	public hasCampaignFeatures: boolean
	public isLegacy: boolean
	public isSelectable: boolean
	public tier: number

	constructor(data: {
		canRequestPlaylists: boolean
		canRequestSchedules: boolean
		hasCampaignFeatures: boolean
		isLegacy: boolean
		isSelectable: boolean
		tier: number
	}) {
		this.canRequestPlaylists = data.canRequestPlaylists
		this.canRequestSchedules = data.canRequestSchedules
		this.hasCampaignFeatures = data.hasCampaignFeatures
		this.isLegacy = data.isLegacy
		this.isSelectable = data.isSelectable
		this.tier = data.tier
	}

	static create(data: Partial<SubscriptionPlanMetadata>) {
		const defaultData = {
			canRequestPlaylists: false,
			canRequestSchedules: false,
			hasCampaignFeatures: false,
			isLegacy: false,
			isSelectable: true,
			tier: 1,
		}

		return new SubscriptionPlanMetadata({ ...defaultData, ...data })
	}
}
